import { ProModal } from "pro-features/components/Modals/src/ProModal";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { Button, IconButton } from "@similarweb/ui-components";
import { Textfield } from "@similarweb/ui-components";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import styled from "styled-components";
import { colorsPalettes } from "@similarweb/styles";

interface IDashboardRenameDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onRename: (newName: string) => void;
    currentName: string;
}

const RenameModalSection = styled.div`
    padding: 24px;
    display: flex;
    flex-direction: row;
`;

const RenameModalHeader = styled(RenameModalSection)`
    justify-content: flex-start;
    align-items: center;

    span {
        font-size: 20px;
        font-weight: 500;
        line-height: 24px;
        color: ${colorsPalettes.carbon[400]};
    }
`;

const RenameModalBody = styled(RenameModalSection)`
    padding-top: 0;
    padding-bottom: 0;
`;

const RenameModalFooter = styled(RenameModalSection)`
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    padding-bottom: 16px;
    padding-right: 16px;
`;

const ButtonContainer = styled.div`
    margin-left: 16px;
`;

const RenameTextfield = styled(Textfield)`
    width: 100%;
`;

const DashboardRenameDialog: FC<IDashboardRenameDialogProps> = ({ isOpen, onClose, onRename, currentName }) => {
    const [inputValue, setInputValue] = useState<string>(currentName);
    const [isLoadingButton, setIsLoadingButton] = useState<boolean>(false);
    const t = useTranslation();

    useEffect(() => {
        setIsLoadingButton(false);
    }, [isOpen]);

    useEffect(() => {
        setInputValue(currentName);
    }, [currentName]);

    const internalOnRename = useCallback(() => {
        setIsLoadingButton(true);
        onRename(inputValue);
    }, [onRename, inputValue]);

    const internalOnClose = useCallback(() => {
        setInputValue(currentName);
        onClose();
    }, [onClose, currentName]);

    return (
        <ProModal
            isOpen={isOpen}
            onCloseClick={internalOnClose}
            customStyles={{ content: { padding: 0, width: 400 } }}
            showCloseIcon={false}
        >
            <RenameModalHeader>
                <span>Rename Dashboard</span>
            </RenameModalHeader>
            <RenameModalBody>
                <RenameTextfield
                    placeholder={t("dashboard.template.title.placeholder")}
                    onChange={(value) => setInputValue(value)}
                    defaultValue={inputValue}
                    autoFocus={true}
                    label={"Enter a new name for the dashboard:"}
                />
            </RenameModalBody>
            <RenameModalFooter>
                <ButtonContainer>
                    <Button
                        type="flat"
                        onClick={internalOnClose}
                    >
                        Cancel
                    </Button>
                </ButtonContainer>
                <ButtonContainer>
                    <IconButton
                        type="primary"
                        iconName="edit-icon"
                        iconSize="xs"
                        onClick={internalOnRename}
                        isLoading={isLoadingButton}
                    >
                        Rename
                    </IconButton>
                </ButtonContainer>
            </RenameModalFooter>
        </ProModal>
    );
};

export default DashboardRenameDialog;
